/** @jsx jsx */
import {useEffect, useState} from 'react'
import {jsx, css, Global} from '@emotion/react'
import {graphql, useStaticQuery} from 'gatsby'
import Img from 'gatsby-image'
import {logDplWithExtensionParams, EVENTS} from '../../../../util/dpl'
import {clearLocalStorage, getExtensionMetadataFromLocalStorage} from '../../../../util/extensionMetadata'
import CheckmarkIcon from '../../../../images/checkmark-icon.svg'
import Layout from '../../../layout'
import SEO from '../../../seo'
import Header from './header'
import {legacyExtensionFavicons} from '../../../../constants'

const Success4624 = ({languageCode = 'en'}) => {
    const site = useStaticQuery(graphql`
        query Success4624Query {
            successGuy: file(relativePath: {eq: "4624/success-guy.png"}) {
                childImageSharp {
                    fixed(height: 352, quality: 100) {
                        ...GatsbyImageSharpFixed_noBase64
                    }
                }
            }
        }
    `)

    const translations = {
        en: {
            pageTitle: 'Success!',
            pageDescription: 'Startpage is now your default search engine.',
            pageSuggestion: 'Try your first private search',
            headline: 'Success! Startpage is now your default search engine.',
            tagline: 'Why search privately with Startpage?',
            bullets: ['No Search History', 'No Ad Targeting', 'No Digital Footprint'],
        },
        de: {
            pageTitle: 'Glückwunsch!',
            pageDescription: 'Startpage ist jetzt deine Standard-Suchmaschine.',
            pageSuggestion: 'Versuchen Sie es mit Ihrer ersten privaten Suche',
            headline: 'Glückwunsch! Startpage ist jetzt deine Standard-Suchmaschine.',
            tagline: 'Warum sollte ich die private Suche von Startpage nutzen?',
            bullets: ['Kein Suchverlauf', 'Keine personalisierte Werbung', 'Kein digitaler Fußabdruck'],
        },
        nl: {
            pageTitle: 'Gelukt!',
            pageDescription: 'Startpage is nu je standaard zoekmachine.',
            pageSuggestion: 'Probeer je eerste privé zoekopdracht',
            headline: 'Gelukt! Startpage is nu je standaard zoekmachine.',
            tagline: 'Waarom zoek privé met Startpage?',
            bullets: ['Geen zoekgeschiedenis', 'Geen gepersonaliseerde advertenties', 'Geen digitale voetafdruk'],
        },
        fr: {
            pageTitle: 'Succès !',
            pageDescription: 'Startpage est maintenant votre moteur de recherche par défaut.',
            pageSuggestion: 'Essayez votre première recherche privée',
            headline: 'Succès ! Startpage est maintenant votre moteur de recherche par défaut.',
            tagline: 'Pourquoi rechercher en privé avec Startpage ?',
            bullets: ['Aucun historique de recherche', 'Aucune publicité personnalisée', 'Aucun empreinte numérique'],
        },
    }

    const text = translations[languageCode]

    const [hasFiredSuccess, setHasFiredSuccess] = useState(false)

    const isTaboola = (source = '') => {
        return source === 'taboola'
    }

    const logTblaS2SConversion = (act = '') => {
        const payload = {
            event_name: 'startpage_install',
            act,
        }
        fetch('https://www.startpage.com/sp/tbla-s2s', {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error(err)
        })
    }

    useEffect(() => {
        if (!hasFiredSuccess) {
            const {source, act} = getExtensionMetadataFromLocalStorage() || {}
            const isTaboolaVar = isTaboola(source)
            if (isTaboolaVar) {
                logTblaS2SConversion(act)
            }
            logDplWithExtensionParams(EVENTS.StartpagePageLoad).then(() => {
                clearLocalStorage()
            })
            setHasFiredSuccess(true)
        }
    }, [hasFiredSuccess])

    return (
        <Layout>
            <Global
                styles={css`
                    body {
                        min-width: 1200px;
                    }
                `}
            />
            <SEO
                title={`${text.pageTitle} ${text.pageDescription}`}
                description={`${text.pageSuggestion}`}
                favicons={legacyExtensionFavicons}
                ogImagePath="https://www.startpage.com/sp/cdn/favicons/extension/sp_icon_128.png"
            />
            <div
                className="container"
                css={css`
                    position: fixed;
                    width: 100%;
                    min-width: 900px;
                `}
            >
                <Header languageCode={languageCode} />
                <section
                    className="main"
                    css={css`
                        background: linear-gradient(180deg, #e2e7ff 0%, #dcfefd 100%);
                        padding: 4rem 1rem 3rem;
                        height: 100vh;
                    `}
                >
                    <div
                        className="inner"
                        css={css`
                            max-width: 700px;
                            margin: 0 auto;
                            text-align: center;
                            @media (max-height: 750px) {
                                transform: scale(0.85) translateY(-60px);
                            }
                        `}
                    >
                        <h1
                            className="headline"
                            css={css`
                                font-weight: 800;
                                font-size: 40px;
                            `}
                        >
                            {text.headline}
                        </h1>
                        <Img
                            fixed={site.successGuy.childImageSharp.fixed}
                            css={css`
                                margin: 2.5rem 0;
                            `}
                        />
                        <div className="tagline">
                            <h2
                                css={css`
                                    font-weight: 700;
                                    font-size: 20px;
                                    margin: 0 0 1rem;
                                `}
                            >
                                {text.tagline}
                            </h2>
                            <div
                                className="bullets"
                                css={css`
                                    display: flex;
                                    justify-content: center;
                                `}
                            >
                                {text.bullets.map((bullet, i) => {
                                    return (
                                        <div
                                            className="bullet"
                                            css={css`
                                                display: flex;
                                                align-items: center;
                                                white-space: nowrap;
                                                margin-right: 20px;
                                                svg {
                                                    transform: scale(0.8);
                                                }
                                                p {
                                                    font-size: 14px;
                                                    font-weight: 500;
                                                    margin-left: 5px;
                                                }
                                            `}
                                        >
                                            <CheckmarkIcon />
                                            <p>{bullet}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    )
}

export default Success4624
